import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { IQa } from "@/api/businessLinesApi";

interface IQaItem {
  id: string;
  sequence_no: number;
  question: string;
  answer: string;
  note: string;
}

interface DndProps {
  qa: IQa;
  activeSectionId: string | null;
  setQa: (qa: IQa) => void;
  activeId: string | null;
  setActiveId: (activeId: string | null) => void;
  onQaInputChange: (
    qaId: string,
    field: "question" | "answer" | "note",
    value: string
  ) => void;
  onRemoveQuestion: (qaId: string) => void;
  isReadOnly?: boolean;
}

export const Dnd = ({
  qa,
  activeSectionId,
  setQa,
  activeId,
  setActiveId,
  onQaInputChange,
  onRemoveQuestion,
  isReadOnly = false,
}: DndProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    if (isReadOnly) return;
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    if (isReadOnly) return;

    const { active, over } = event;

    if (over && active.id !== over.id) {
      const activeSection = qa.find(
        (section) => section.id === activeSectionId
      );
      if (activeSection) {
        const oldIndex = activeSection.qa_list.findIndex(
          (item) => item.id === active.id
        );
        const newIndex = activeSection.qa_list.findIndex(
          (item) => item.id === over.id
        );

        setQa(
          qa.map((section) =>
            section.id === activeSectionId
              ? {
                  ...section,
                  qa_list: arrayMove(section.qa_list, oldIndex, newIndex),
                }
              : section
          )
        );
      }
    }

    setActiveId(null);
  };

  const activeSection = qa.find((section) => section.id === activeSectionId);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={activeSection?.qa_list.map((qa) => qa.id) || []}
        strategy={verticalListSortingStrategy}
      >
        {activeSection?.qa_list.map((qa) => (
          <SortableQaCard
            key={qa.id}
            qa={qa}
            onQaInputChange={onQaInputChange}
            onRemoveQuestion={onRemoveQuestion}
            isDragging={activeId === qa.id}
            isReadOnly={isReadOnly}
          />
        ))}
      </SortableContext>
    </DndContext>
  );
};

const SortableQaCard = ({
  qa,
  onQaInputChange,
  onRemoveQuestion,
  isDragging,
  isReadOnly = false,
}: {
  qa: IQaItem;
  onQaInputChange: (
    qaId: string,
    field: "question" | "answer" | "note",
    value: string
  ) => void;
  onRemoveQuestion: (qaId: string) => void;
  isDragging: boolean;
  isReadOnly?: boolean;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: qa.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className="bg-[#F4F7FB] rounded-xl mb-6 border border-[#E5EAF2] relative"
    >
      {/* Card Header */}
      <div
        {...(isReadOnly ? {} : listeners)}
        className={`flex items-center justify-between px-4 py-2 border-b border-[#E5EAF2] rounded-t-xl bg-[#F4F7FB] ${
          isReadOnly ? "" : "cursor-move"
        }`}
      >
        <div className="flex items-center gap-2">
          <span className="text-gray-400">⇅</span>
          <span className="font-semibold text-gray-700 text-base">
            More Question{" "}
            <span className="text-xs text-gray-400">(Simple)</span>
          </span>
          {!isReadOnly && (
            <button
              className="ml-2 text-gray-400 hover:text-[#78C7D1]"
              title="Edit question"
              aria-label="Edit question"
            >
              <svg width="16" height="16" fill="none" viewBox="0 0 24 24">
                <path
                  d="M4 21v-2a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v2"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <circle
                  cx="12"
                  cy="7"
                  r="4"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}
        </div>
        {!isReadOnly && (
          <button
            className="text-gray-400 hover:text-red-400 text-xl font-bold"
            onClick={() => onRemoveQuestion(qa.id)}
          >
            ×
          </button>
        )}
      </div>
      {/* Card Body */}
      <div className="p-4">
        <div className="mb-4">
          <label className="block font-semibold text-gray-700 mb-1">
            Question
          </label>
          <input
            type="text"
            value={qa.question}
            onChange={(e) => onQaInputChange(qa.id, "question", e.target.value)}
            className="w-full border border-[#E5EAF2] rounded-lg px-3 py-2 bg-white text-gray-700 focus:outline-none"
            placeholder="Enter your question..."
            disabled={isReadOnly}
          />
        </div>
        <div className="mb-4">
          <label className="block font-semibold text-gray-700 mb-1">
            Answer
          </label>
          <input
            type="text"
            value={qa.answer}
            onChange={(e) => onQaInputChange(qa.id, "answer", e.target.value)}
            className="w-full border border-[#E5EAF2] rounded-lg px-3 py-2 bg-white text-gray-700 focus:outline-none"
            placeholder="Enter your answer..."
            disabled={isReadOnly}
          />
        </div>
        <div>
          <label className="block font-semibold text-gray-700 mb-1">Note</label>
          <textarea
            value={qa.note}
            onChange={(e) => onQaInputChange(qa.id, "note", e.target.value)}
            className="w-full border border-[#E5EAF2] rounded-lg px-3 py-2 bg-white text-gray-700 focus:outline-none"
            rows={3}
            placeholder="Please answer the question below, otherwise the addressee will be referred to the expert Mr./Ms. ..."
            disabled={isReadOnly}
          />
        </div>
      </div>
    </div>
  );
};
